import React from 'react';

import styled from 'styled-components';

import { OnClick } from '@rbi-ctg/frontend';

const getColor = (
  color?: 'primary' | 'secondary' | 'inverted',
  active?: boolean,
  disabled?: boolean
): string => {
  if (disabled) {
    if (color === 'inverted') {
      return 'rgba(255, 255, 255, 0.5)';
    }
    return Styles.color.grey.three;
  }
  if (active) {
    if (color === 'inverted') {
      return Styles.color.primary;
    }
    return Styles.color.white;
  }
  if (color === 'inverted') {
    return Styles.color.white;
  }
  return Styles.color.black;
};

const getBackgroundColor = (
  color?: 'primary' | 'secondary' | 'inverted',
  active?: boolean,
  disabled?: boolean
): string => {
  if (disabled) {
    if (color === 'inverted') {
      return 'rgba(255, 255, 255, 0.05)';
    }
    return Styles.color.grey.seven;
  }
  if (active) {
    if (color === 'inverted') {
      return Styles.color.white;
    }
    return Styles.color.primary;
  }
  if (color === 'inverted') {
    return 'transparent';
  }
  // inactive
  return Styles.color.white;
};

const getBackgroundColorHover = (
  color?: 'primary' | 'secondary' | 'inverted',
  active?: boolean,
  disabled?: boolean
): string => {
  if (disabled) {
    if (color === 'inverted') {
      return 'rgba(255, 255, 255, 0.05)';
    }
    return Styles.color.grey.eight;
  }
  if (active) {
    if (color === 'inverted') {
      return Styles.color.white;
    }
    return Styles.color.primary;
  }
  if (color === 'inverted') {
    return 'rgba(255, 255, 255, 0.05)';
  }
  return 'rgba(0, 0, 0, 0.05)';
};

const getBorderColor = (color?: 'primary' | 'secondary' | 'inverted', active?: boolean): string => {
  if (active) {
    if (color === 'inverted') {
      return 'rgba(255, 255, 255, 0.5)';
    }
    return Styles.color.primary;
  }
  if (color === 'inverted') {
    return 'rgba(255, 255, 255, 0.5)';
  }
  return 'rgba(0, 0, 0, 0.3)';
};

interface IActive {
  active?: boolean;
}

interface IColorable {
  color?: 'primary' | 'secondary' | 'inverted';
}

interface IDisableable {
  disabled?: boolean;
}

export interface IRadioGroup {
  color?: 'primary' | 'secondary' | 'inverted';
  selected: number | string | undefined;
  onChange: (newValue: number | string) => void;
  children: React.ReactNode[];
}

const RadioGroupStyle = styled.div<IActive & IColorable & IDisableable>`
  display: flex;
  flex-direction: row;
  width: 100%;
  margin: 0;
  padding: 0;

  border-radius: 4px;
  border: 1px solid transparent;
  border-color: ${p => getBorderColor(p.color, p.active)};
`;

export const RadioGroup = ({ selected, onChange, children, color }: IRadioGroup) => {
  if (!children || children.length <= 0) {
    return null;
  }

  const RadioGroupContents = children.map((child: any) => {
    const handleClick = (e: React.ChangeEvent<{}>) => {
      e.preventDefault();
      onChange(child.props.value);
    };
    return React.cloneElement(child, {
      color,
      id: child.props.id || child.props.value,
      key: `RadioGroup_${child.props.value}`,
      active: child.props.value === selected,
      onClick: handleClick,
    });
  });

  return (
    <RadioGroupStyle role="radiogroup" color={color}>
      {RadioGroupContents}
    </RadioGroupStyle>
  );
};

const RadioGroupButtonStyle = styled.button<IActive & IColorable & IDisableable>`
  width: 100%;
  font-size: 1rem;
  font-family: ${Styles.fontFamily.base};
  color: ${p => getColor(p.color, p.active, p.disabled)};
  list-style-type: none;
  margin: 0;
  padding: 0.8rem 0;
  border: none;
  transition: transform 200ms ease-out;
  cursor: ${p => (p.disabled || p.active ? 'default' : 'pointer')};
  color: ${p => getColor(p.color, p.active, p.disabled)};
  background-color: ${p => getBackgroundColor(p.color, p.active, p.disabled)};
  font-weight: ${props => (props.active ? Styles.fontWeight.heavy : Styles.fontWeight.normal)};
  border-right: 1px solid transparent;

  &:not(:last-child) {
    border-right-color: ${p => getBorderColor(p.color, p.active)};
  }
  &:hover {
    background-color: ${p => getBackgroundColorHover(p.color, p.active, p.disabled)};
  }
  &:first-child {
    border-top-left-radius: 3px;
    border-bottom-left-radius: 3px;
  }
  &:last-child {
    border-top-right-radius: 3px;
    border-bottom-right-radius: 3px;
  }
  &:focus {
    outline: 2px solid transparent;
    outline-color: ${p => getBorderColor(p.color, p.active)};
  }
`;

export interface IRadioGroupButton {
  id?: string;
  active?: boolean;
  label: string;
  value: number | string;
  onClick?: OnClick;
  ariaControls?: string;
  disabled?: boolean;
  color?: 'primary' | 'secondary' | 'inverted';
}

export const RadioGroupButton = ({
  id,
  label,
  active,
  disabled,
  color,
  onClick,
}: IRadioGroupButton) => {
  return (
    <RadioGroupButtonStyle
      id={id}
      role="radio"
      aria-checked={active}
      color={color}
      disabled={disabled}
      active={active}
      onClick={onClick}
    >
      {label}
    </RadioGroupButtonStyle>
  );
};
